import React, { useRef, useEffect, useMemo } from 'react'
import useDiscussionDialog from './useDiscussionDialog';
import useDiscussionMessage, {ADMIN_GROUPS, MESSAGE_STATUSES} from './useDiscussionMessage';
import useDiscussionSettings from './useDiscussionSettings';
import { Button } from '../../bootstrap';
import LoadingIndicator from '../loading_indicator';
import Tooltip from '../Tooltip';
import Scrollbars from "react-custom-scrollbars-2";
import LexicalDiscussionEditor, { BLANK_CONTENT } from '../richTextPlugins/LexicalDiscussionEditor.lazy.jsx';

const USER_COLOR_STYLE = {
    bubble: {
        self: '#daefee',
        fund: '#e5e6e8',
        project: '#d6f0fb'
    }
}

const CLOSED_BACKGROUND_COLOR = '#e5e6e8'

const PREVIEW_MESSAGE_COUNT = 3

const DiscussionThread = ({
    threadContent, 
    live, 
    user_info, 
    person_id, 
    permissions, 
    setLiveThread, 
    deleteThread, 
    reopenThread, 
    target_type, 
    unreadDiscussions, 
    setExpandedThread, 
    expandedThread, 
    elementId, 
    is_fund_staff, 
    show_user_names, 
    project_id
}) => {
    const scrollRef = useRef(null)
    const {       
        messages,
        handleScroll,
        cancelEditing,
        sendMessage,
        setContent,
        content,
        loading,
        setLoading,
        reloadMessages,
        editingId,
        handleDeleteThread,
        handleReopenThread,
        usersInfo,
        setEditingId} = useDiscussionDialog(
        {deleteThread, reopenThread},
        {
            target_type: target_type,
            discussion_id: threadContent.id,
            messages: threadContent.messages,
            live,
            scrollRef,
            expandedThread,
            user_info
        })
    const firstMessage = (messages.length > 0 ? messages[0] : {}) || threadContent.first_message || {}
    const showMessages = messages.filter((m) => m.status != 'deleted' && m.id != firstMessage.id)
    const expanded = threadContent.messages.length <= PREVIEW_MESSAGE_COUNT || expandedThread === threadContent.id;
    const threadBorderColor = threadContent.closed ? '#999999' : ((unreadDiscussions || []).includes(threadContent.id) ? '#20bf6b' :  target_type === 'programme' ? '#4cb6b2' : '#78bedb')
    const handleReplyComment = () =>{
        setLiveThread(threadContent.id);
    }

    const cancelEdit = () => {
        cancelEditing();
        setLiveThread(null);
    }

    useEffect(() => {
        if (expandedThread === threadContent.id){
            scrollRef.current?.scrollToBottom();
        }
    }, [expandedThread])

    const messageComponents = () => {
        return (
            (expanded ? showMessages : showMessages.slice(showMessages.length-PREVIEW_MESSAGE_COUNT, showMessages.length)).map((m,i) => 
            <DiscussionMessage
                message={m} 
                user={(usersInfo || {})[m.person_id] || {}} 
                owner={person_id === m.person_id}
                discussion_id={threadContent.id}
                closed={threadContent.closed}
                key={`m-${m.id}`}
                className='pl-3'
                {...{permissions, setLoading, setEditingId, setContent, reloadMessages, setLiveThread, is_fund_staff, show_user_names}} />)
        )
    }

    const filePermissions = useMemo(() => ({
        'reply_activity_feed_discussion': {
            id: project_id,
            type: 'project'
        }
    }), [])


    return (
        <div className='discussion-thread-content mb-1' id={elementId}
            style={{
                borderLeft: `7px solid ${threadBorderColor}`,
                backgroundColor: threadContent.closed ? CLOSED_BACKGROUND_COLOR : 'white',
                display: (!expandedThread || expandedThread === threadContent.id) ? 'block' : 'none' }} >
                <div className='discussion-body'>
                    {loading && messages.length === 0 ? <LoadingIndicator size='sm' /> :
                    <>
                        {Object.keys(firstMessage).length > 0 &&
                        <DiscussionMessage
                        message={firstMessage} 
                        user={(usersInfo || {})[firstMessage.person_id] || {}} 
                        owner={person_id === firstMessage.person_id}
                        discussion_id={threadContent.id}
                        closed={threadContent.closed}
                        titleMessage={true}
                        permissions={{...permissions, delete: false}} // Do not allow delete first message
                        {...{setLoading, setEditingId, setContent, reloadMessages, setLiveThread, is_fund_staff, show_user_names}} />}
                        {expandedThread === threadContent.id ? 
                        <Scrollbars 
                            style={{height: `${messages.length*45}px`, maxHeight: target_type === 'project' ? '50vh' : '100%', width: '100%'}}
                            onScroll={handleScroll}
                            ref={scrollRef}>
                                {messageComponents()}
                        </Scrollbars> : 
                        messageComponents()}
                    </>
                    }
                </div>
            {
                expanded ?
                <div className='discussion-footer'>
                    {
                        threadContent.closed ?
                        <Button type='default' size='sm' disabled={!permissions.reopen_thread} onClick={handleReopenThread} className='mt-1'>
                            {permissions.reopen_thread ? I18n.t('reopen_thread') : I18n.t('thread_closed')}
                        </Button>
                        :
                        <>
                            { live &&
                            <>
                                {editingId !== null ? 
                                <div className='chat-editing-desc'><i className="fa fa-xs fa-pencil" style={{marginRight: '2px'}}></i>{I18n.t('editing_message')}</div> :
                                <span style={{fontSize: '12px'}}>{I18n.t('replying_to_thread')}</span>
                                }
                                <LexicalDiscussionEditor
                                    editorContainerStyle={{width: '100%', margin: 0}}
                                    value={content} 
                                    placeholder={I18n.t('type_your_message')} 
                                    updateAnswer={(v) => setContent(v)}
                                    onChange={(v) => setContent(v)} 
                                    editorStyle={{backgroundColor: editingId !== null ? '#e5e6e8' : 'white', marginTop: '0.2rem', minHeight: '60px'}}
                                    reset={content === ''}
                                    editing={editingId !== null}
                                    filePermissions={filePermissions}
                                />
                                <div className='chat-area-footer'>
                                    {<Button type='default' size='sm' className='pull-left' onClick={cancelEdit}>
                                        {I18n.t('cancel')}
                                    </Button>}
                                    <Button type='primary' size='sm' className='pull-right' onClick={sendMessage} disabled={loading || content.length === 0 || content === BLANK_CONTENT}>
                                        {I18n.t('send')}
                                    </Button>
                                </div>
                            </>}
                            {permissions.reply && !live && <Button type='primary' size='sm' onClick={handleReplyComment} className='mr-1 mt-1'>{I18n.t('reply')}</Button>}
                            {permissions.delete_thread && !live && <Button type='default' size='sm' onClick={handleDeleteThread} className='mt-1'>{I18n.t('close_thread')}</Button>}
                        </>
                    }
                </div>
                :
                <div className='expand-replies' onClick={() => setExpandedThread(threadContent.id)}>
                    {I18n.t('expand_replies')}
                </div>
            }
        </div>
    );
}

const DiscussionMessage = ({message, user = {}, owner,discussion_id, titleMessage = false, show_user_names, ...rest}) => {
    const { setContent, setEditingId, reloadMessages, setLoading, permissions, setLiveThread, className, is_fund_staff, closed } = rest
    const {isHovered, setIsHovered, timeOut, user_logo_path, programme_logo_path} = useDiscussionMessage({user, owner, message, setContent, setEditingId});
    const bubbleColor = closed ? CLOSED_BACKGROUND_COLOR : (owner ? USER_COLOR_STYLE.bubble.self : ADMIN_GROUPS.includes(user.user_group) ? USER_COLOR_STYLE.bubble.fund : USER_COLOR_STYLE.bubble.project);
    const showName = show_user_names || is_fund_staff || !ADMIN_GROUPS.includes(user.user_group);

    const {handleDeleteMessage, handleEditMessage} = useDiscussionSettings({message, reloadMessages, setLoading, setContent, setEditingId})

    const onClickDeleteMessage = () => {
        if (window.confirm(I18n.t('delete_reply_confirm'))){
            setLiveThread(discussion_id);
            handleDeleteMessage();
        }
    }

    const onClickEditMessage = () => {
        setLiveThread(discussion_id);
        handleEditMessage();
    }
    return (
        <div className={`chat-bubble-container ${className}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <div className='chat-bubble pull-left'>
            <div className='message-with-user'>
                {
                    showName ? (user_logo_path && <img src={user_logo_path} className='user-icon' alt=""/>)
                    : (programme_logo_path && <img src={programme_logo_path} className='user-icon' alt=""/>)
                }
                <div style={{position: 'absolute'}}>
                    {(permissions.edit && isHovered && owner && !timeOut && !closed) && <button onClick={onClickEditMessage} className='btn btn-default edit-button'><i className="fa fa-xs fa-pencil"> </i></button>}
                    {(permissions.delete && isHovered && owner && !timeOut && !closed) && <button onClick={onClickDeleteMessage} className='btn btn-default delete-button'><i className="fa fa-xs fa-trash-o"> </i></button>}
                </div>
                <div className='bubble-header-container' style={{width: titleMessage ? '100%' : '90%'}}>
                    <div className='bubble-header'>
                        <div className='pull-left'>
                        {
                            showName ? <span className='name-weight'>{`${user.full_name} - ${user.user_type}`}</span> 
                            : <span className='name-weight'>{I18n.t('tenant_team', {id: user.project_identifier})}</span>
                        }
                        </div>
                        <div className='pull-right'>
                            {   message.status === MESSAGE_STATUSES['edited'] && message.updated_at !== null &&   
                                <Tooltip title={I18n.t('message_edited_at', {time: message.updated_at_label})} trigger='hover'>
                                    <i className="fa fa-xs fa-pencil" style={{marginRight: '2px'}}></i>
                                </Tooltip>
                            }
                            <span className='color-grey'>{message.created_at_label}</span>
                        </div>
                    </div>
                    <LexicalDiscussionEditor
                        value={message.content}
                        editorContainerClassNames='bubble-content message'
                        editorContainerStyle={{margin: 0, padding: 0}}
                        editorStyle={{backgroundColor: bubbleColor, minHeight: 0, padding: '0px 4px 0px 4px'}}
                        readOnly={true}
                        preview={true}
                        updateReadOnly={true}
                        transparentBackground={true}
                    />
                </div>
            </div>
        </div>
    </div>
    )
}

export default DiscussionThread;